/**
 * Grid, axes, ticks, labels.
 *
 * Chrome is deliberately recessive — hairline grid, muted ticks — so the data
 * carries the contrast. Zero lines are drawn slightly stronger because in
 * physics they mean something (equilibrium, the sign change) rather than being
 * just another gridline.
 */

import type { Layout } from "./geometry.ts";
import { ticks, type Scale, type Tick } from "./scales.ts";
import { CHROME, type ThemeMode } from "./palette.ts";

interface AxesProps {
  readonly layout: Layout;
  readonly xScale: Scale;
  readonly yScale: Scale;
  readonly mode: ThemeMode;
  readonly showGrid: boolean;
}

export function Axes({
  layout,
  xScale,
  yScale,
  mode,
  showGrid,
}: AxesProps) {
  const chrome = CHROME[mode];
  const xTicks = ticks(xScale, 7);
  const yTicks = ticks(yScale, 6);

  const plotRight = layout.left + layout.innerWidth;
  const plotBottom = layout.top + layout.innerHeight;

  return (
    <g className="plot-axes">
      {showGrid && (
        <g stroke={chrome.grid} strokeWidth={1} shapeRendering="crispEdges">
          {xTicks.map((tick) => (
            <line
              key={`gx-${tick.value}`}
              x1={xScale.to(tick.value)}
              x2={xScale.to(tick.value)}
              y1={layout.top}
              y2={plotBottom}
            />
          ))}
          {yTicks.map((tick) => (
            <line
              key={`gy-${tick.value}`}
              x1={layout.left}
              x2={plotRight}
              y1={yScale.to(tick.value)}
              y2={yScale.to(tick.value)}
            />
          ))}
        </g>
      )}

      <ZeroLines
        layout={layout}
        xScale={xScale}
        yScale={yScale}
        color={chrome.axis}
      />

      {/* Frame */}
      <rect
        x={layout.left}
        y={layout.top}
        width={layout.innerWidth}
        height={layout.innerHeight}
        fill="none"
        stroke={chrome.axis}
        strokeWidth={1}
        shapeRendering="crispEdges"
      />

      <g
        fill={chrome.muted}
        fontSize={13}
        style={{ fontVariantNumeric: "tabular-nums" }}
      >
        {xTicks.map((tick) => (
          <TickLabel
            key={`tx-${tick.value}`}
            tick={tick}
            x={xScale.to(tick.value)}
            y={plotBottom + 18}
            anchor="middle"
          />
        ))}
        {yTicks.map((tick) => (
          <TickLabel
            key={`ty-${tick.value}`}
            tick={tick}
            x={layout.left - 10}
            y={yScale.to(tick.value) + 4}
            anchor="end"
          />
        ))}
      </g>

    </g>
  );
}

function TickLabel({
  tick,
  x,
  y,
  anchor,
}: {
  tick: Tick;
  x: number;
  y: number;
  anchor: "middle" | "end";
}) {
  return (
    <text x={x} y={y} textAnchor={anchor}>
      {tick.label}
    </text>
  );
}

/** x = 0 and y = 0, drawn only when they fall inside the window. */
function ZeroLines({
  layout,
  xScale,
  yScale,
  color,
}: {
  layout: Layout;
  xScale: Scale;
  yScale: Scale;
  color: string;
}) {
  const showY = inside(xScale.domain, 0) && xScale.kind === "linear";
  const showX = inside(yScale.domain, 0) && yScale.kind === "linear";

  return (
    <g stroke={color} strokeWidth={1.5} shapeRendering="crispEdges">
      {showY && (
        <line
          x1={xScale.to(0)}
          x2={xScale.to(0)}
          y1={layout.top}
          y2={layout.top + layout.innerHeight}
        />
      )}
      {showX && (
        <line
          x1={layout.left}
          x2={layout.left + layout.innerWidth}
          y1={yScale.to(0)}
          y2={yScale.to(0)}
        />
      )}
    </g>
  );
}

const inside = (domain: readonly [number, number], value: number): boolean =>
  value > Math.min(...domain) && value < Math.max(...domain);
